import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { CodeEditor } from "../components/CodeEditor.tsx";
import { Markdown } from "../components/Markdown.tsx";
import { highlightCode } from "../components/highlight.tsx";
import { runDrill, type RunResult } from "../runner/run.ts";
import { api, type AttemptResult, type Stats } from "../api.ts";
import { formatDuration } from "../scheduler.ts";
import type { Queued } from "../scheduler.ts";
import { analytics } from "../analytics.ts";
import { reportError } from "../instrument.ts";

const GRADE_LABEL = ["Failed", "Hard", "Good", "Easy"] as const;

/**
 * What each rung of the ladder leaves of the score, for display only.
 *
 * The server owns these numbers and applies them; this copy exists so the offer
 * can state its price before you pay it. Quoting a cost you only discover
 * afterwards is how a feature like this becomes a trap.
 */
const HINT_KEEPS = [1, 0.75, 0.55, 0.4];

/**
 * When to stop being quiet about the ladder.
 *
 * Not a nag and not a timer: the row is always there, and this only changes how
 * loudly it sits once someone is plausibly stuck rather than thinking.
 */
function looksStuck(elapsedMs: number, budgetMs: number, runs: number): boolean {
  return elapsedMs > budgetMs * 0.6 || runs >= 3;
}

type Props = {
  queued: Queued;
  stats: Stats;
  dueNow: number;
  onGraded: (result: AttemptResult) => void;
  onNext: () => void;
  /**
   * Nobody is signed in, so there is no schedule to move and no score to award.
   * The drill itself is identical: same bank, same hidden tests, same clock,
   * same blocked paste. What a guest does not get is the thing the account is
   * actually for, which is the attempt being remembered.
   */
  guest?: boolean;
};

/** What a guest gets instead of a grade. No score, because scoring is the server's. */
type GuestVerdict = { passed: boolean; revealed: boolean; elapsedMs: number };

export function DrillScreen({ queued, stats, dueNow, onGraded, onNext, guest = false }: Props) {
  const { drill, reason, progress } = queued;

  const [code, setCode] = useState(drill.starter);
  const [result, setResult] = useState<RunResult | null>(null);
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [graded, setGraded] = useState<AttemptResult | null>(null);
  const [guestVerdict, setGuestVerdict] = useState<GuestVerdict | null>(null);
  const [revealed, setRevealed] = useState(false);
  // Rungs of the hint ladder taken, and therefore also how many are on screen.
  const [hintsTaken, setHintsTaken] = useState(0);
  // Which face of the left panel is showing. The hints carry code now, so they
  // need the same width and the same monospace column the statement gets; they
  // cannot be a footnote under it.
  const [tab, setTab] = useState<"problem" | "hints">("problem");
  const [toast, setToast] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const startedAt = useRef(Date.now());
  // Read inside callbacks that must not be rebuilt on every keystroke.
  const latest = useRef({ code, runs, revealed, graded, hintsTaken });
  latest.current = { code, runs, revealed, graded, hintsTaken };

  // Reset everything when a different drill is served. Keyed on the id rather
  // than the object so a refetch that returns an equal drill does not wipe work
  // in progress.
  useEffect(() => {
    setCode(drill.starter);
    setResult(null);
    setRuns(0);
    setElapsed(0);
    setGraded(null);
    setRevealed(false);
    setHintsTaken(0);
    setTab("problem");
    setSubmitError(null);
    startedAt.current = Date.now();
  }, [drill.id, drill.starter]);

  // The clock keeps running past the budget rather than cutting you off. Going
  // over costs you the grade, which is the honest penalty; being locked out
  // mid-thought teaches nothing.
  useEffect(() => {
    if (graded) return;
    const id = setInterval(() => setElapsed(Date.now() - startedAt.current), 250);
    return () => clearInterval(id);
  }, [graded]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(id);
  }, [toast]);

  const remaining = drill.timeBudgetMs - elapsed;
  const overtime = remaining < 0;

  const submit = useCallback(
    async (passed: boolean, wasRevealed: boolean) => {
      const facts = {
        drillId: drill.id,
        pattern: drill.pattern,
        passed,
        elapsedMs: Math.min(Date.now() - startedAt.current, 6 * 60 * 60 * 1000),
        timeBudgetMs: drill.timeBudgetMs,
        runs: latest.current.runs,
        revealed: wasRevealed,
        basePoints: drill.basePoints,
        hintsUsed: latest.current.hintsTaken,
      };
      // A guest attempt is never sent. There is no account to attach it to, and
      // inventing a client-side score would quietly fork the one the server owns.
      if (guest) {
        setGuestVerdict({ passed, revealed: wasRevealed, elapsedMs: facts.elapsedMs });
        analytics.drillGraded({
          drillId: drill.id,
          pattern: drill.pattern,
          grade: passed && !wasRevealed ? 2 : 0,
          score: 0,
          elapsedMs: facts.elapsedMs,
          runs: facts.runs,
          revealed: facts.revealed,
          ahead: false,
          hintsUsed: facts.hintsUsed,
        });
        return;
      }
      try {
        const outcome = await api.attempt(facts);
        setGraded(outcome);
        onGraded(outcome);
        analytics.drillGraded({
          drillId: drill.id,
          pattern: drill.pattern,
          grade: outcome.grade,
          score: outcome.score,
          elapsedMs: facts.elapsedMs,
          runs: facts.runs,
          revealed: facts.revealed,
          ahead: outcome.ahead,
          hintsUsed: facts.hintsUsed,
        });
      } catch (cause) {
        reportError(cause, { where: "DrillScreen.submit", drillId: drill.id });
        // Never lose the attempt to a network blip: show the result locally and
        // say plainly that the schedule did not move.
        setSubmitError("Could not save this attempt. Your schedule has not been updated.");
        setGraded({
          grade: passed && !wasRevealed ? 2 : 0,
          score: 0,
          breakdown: { base: drill.basePoints, speed: 0, runs: 0, hints: 0, streak: 0, total: 0 },
          ahead: false,
          progress: {
            drillId: drill.id,
            pattern: drill.pattern,
            attempts: (progress?.attempts ?? 0) + 1,
            passes: progress?.passes ?? 0,
            streak: 0,
            ease: 2.5,
            intervalDays: 0,
            dueAt: new Date().toISOString(),
            bestScore: 0,
            bestTimeMs: null,
            lastGrade: null,
            lastAttemptAt: new Date().toISOString(),
          },
          stats,
        });
      }
    },
    [drill, onGraded, progress, stats, guest],
  );

  /**
   * What the harness generates its inputs from.
   *
   * How many times this drill has been attempted, so a drill that comes round
   * again next week is not the drill you memorised, while every Run inside one
   * attempt sees exactly the same inputs. Tying it to the wall clock instead
   * would move the target between two runs a minute apart.
   */
  const seed = progress?.attempts ?? 0;

  const run = useCallback(async () => {
    if (latest.current.graded || running) return;
    setRunning(true);
    const nextRuns = latest.current.runs + 1;
    setRuns(nextRuns);
    const outcome = await runDrill(latest.current.code, drill.harness, drill.prelude, seed);
    setResult(outcome);
    setRunning(false);
    const passed =
      !outcome.error && outcome.outcomes.length > 0 && outcome.outcomes.every((o) => o.passed);
    analytics.drillRun({ drillId: drill.id, pattern: drill.pattern, run: nextRuns, passed });
    if (passed) {
      // Passing ends the attempt. There is no separate submit button: in a real
      // screen the moment everything goes green is the moment you are done.
      await submit(true, latest.current.revealed);
    }
  }, [drill.harness, drill.prelude, drill.id, drill.pattern, running, seed, submit]);

  const takeHint = useCallback(() => {
    if (latest.current.graded) return;
    const rung = latest.current.hintsTaken + 1;
    if (rung > drill.hints.length) return;
    setHintsTaken(rung);
    setTab("hints");
    analytics.drillHint({
      drillId: drill.id,
      pattern: drill.pattern,
      rung,
      elapsedMs: Date.now() - startedAt.current,
    });
  }, [drill.hints.length, drill.id, drill.pattern]);

  const giveUp = useCallback(async () => {
    setRevealed(true);
    await submit(false, true);
  }, [submit]);

  const visible = result?.outcomes.filter((o) => !o.hidden) ?? [];
  const hidden = result?.outcomes.filter((o) => o.hidden) ?? [];
  const passedCount = result?.outcomes.filter((o) => o.passed).length ?? 0;
  // Locked means the attempt is over, whoever made it. Everything that used to
  // key off `graded` keys off this instead, or a guest would keep an editable
  // editor and a hidden solution after finishing.
  const locked = graded != null || guestVerdict != null;
  const showSolution = locked;
  const failed = result?.outcomes.filter((o) => !o.passed) ?? [];
  // Every rung once it is over: the teaching is the point, and there is nothing
  // left to protect after the attempt has been graded.
  const shownHints = locked ? drill.hints : drill.hints.slice(0, hintsTaken);
  const offerLoudly =
    !locked && hintsTaken === 0 && looksStuck(elapsed, drill.timeBudgetMs, runs);

  const reasonLabel = useMemo(() => {
    if (reason === "new") return "new";
    if (reason === "due") return "due";
    return "extra rep";
  }, [reason]);

  return (
    <div className="drill">
      <header className="bar">
        <div className="bar-left">
          {/* In the bar rather than floating over it: a fixed-position link in
              the corner landed underneath the clock and was unclickable. */}
          <Link className="leave" to={guest ? "/" : "/profile"} title="Leave this drill">
            ←
          </Link>
          <span className="chip">{drill.pattern}</span>
          <h1>{drill.title}</h1>
          <span className={`chip chip-${reason}`}>{reasonLabel}</span>
        </div>
        <div className="bar-right">
          {/* Four zeroes would be worse than no numbers: they read as a broken
              account rather than as no account. */}
          {guest ? (
            <span className="chip chip-guest" title="Nothing here is being saved">
              not signed in
            </span>
          ) : (
            <>
              <Stat label="streak" value={`${stats.dayStreak}d`} />
              <Stat label="today" value={`${stats.todayPasses}/${stats.todayAttempts}`} />
              <Stat label="score" value={stats.totalScore.toLocaleString()} />
              <Stat label="due" value={String(dueNow)} />
            </>
          )}
          <div className={`clock ${overtime ? "over" : remaining < 120_000 ? "low" : ""}`}>
            {overtime ? `+${formatDuration(-remaining)}` : formatDuration(remaining)}
          </div>
        </div>
      </header>

      <div className="columns">
        <section className="panel prose">
          {/* Two faces, not one long scroll. Everything below used to stack
              under the statement, so a hint carrying twenty lines of code
              landed a screen and a half beneath the thing it was explaining. */}
          <nav className="tabs" role="tablist">
            <button
              role="tab"
              aria-selected={tab === "problem"}
              className={tab === "problem" ? "on" : ""}
              onClick={() => setTab("problem")}
            >
              Problem
            </button>
            <button
              role="tab"
              aria-selected={tab === "hints"}
              className={`${tab === "hints" ? "on" : ""}${offerLoudly ? " tab-loud" : ""}`}
              onClick={() => setTab("hints")}
            >
              {locked ? "Solution" : "Hints"}
              {!locked && hintsTaken > 0 && (
                <span className="tab-badge">
                  {hintsTaken}/{drill.hints.length}
                </span>
              )}
            </button>
          </nav>

          <div className={tab === "problem" ? "" : "tab-hidden"}>
          <div className="meta">
            <span>{drill.difficulty}</span>
            <span>{drill.basePoints} pts</span>
            <span>{Math.round(drill.timeBudgetMs / 60000)} min</span>
            {progress && <span>seen {progress.attempts}×</span>}
          </div>
          <Markdown source={drill.statement} />

          {result && (
            <div className="results">
              <h3>
                Tests {passedCount}/{result.outcomes.length}
              </h3>
              {result.error && (
                <div className="failure">
                  <strong>
                    {result.error.phase === "compile"
                      ? result.error.syntax
                        ? `Syntax error on line ${result.error.syntax.line}`
                        : "Did not compile"
                      : result.error.phase === "timeout"
                        ? "Timed out"
                        : "Crashed"}
                  </strong>
                  <p className="failure-message">{result.error.message}</p>
                  {result.error.syntax && (
                    <CodeFrame code={code} line={result.error.syntax.line} column={result.error.syntax.column} />
                  )}
                </div>
              )}
              {visible.map((o) => (
                <Outcome
                  key={o.name}
                  name={o.name}
                  passed={o.passed}
                  message={o.message}
                  about={o.about}
                />
              ))}
              {hidden.length > 0 && (
                <>
                  <h4>Hidden tests</h4>
                  {hidden.map((o) => (
                    <Outcome
                      key={o.name}
                      name={showSolution ? o.name : "hidden case"}
                      passed={o.passed}
                      message={showSolution ? o.message : undefined}
                      about={showSolution ? o.about : undefined}
                    />
                  ))}
                </>
              )}
              {result.logs.length > 0 && (
                <>
                  <h4>Console</h4>
                  <pre className="logs">{result.logs.join("\n")}</pre>
                </>
              )}
            </div>
          )}

          {locked && failed.length > 0 && (
            <div className="missed">
              <h3>What you missed</h3>
              <p className="muted small">
                Every case says what rule it was protecting. These are the ones that caught you.
              </p>
              <ul className="missed-list">
                {failed.map((o) => (
                  <li key={o.name}>
                    <strong>{o.name}</strong>
                    {o.about && <span className="missed-why">{o.about}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {locked && (
            <button className="to-solution" onClick={() => setTab("hints")}>
              Read the full solution →
            </button>
          )}

          </div>

          <div className={tab === "hints" ? "hints" : "hints tab-hidden"}>
            {shownHints.length === 0 && (
              <>
                <p className="muted small">
                  A hint is not a fail. It caps the grade, so this comes back sooner instead of
                  costing you the attempt. Asking is how the schedule finds out you do not know this
                  yet.
                </p>
                <p className="muted small">
                  Each rung shows a real piece of the solution, and the last one shows all of it.
                </p>
              </>
            )}

            {shownHints.map((hint, i) => {
              // The last rung is the whole solution, rendered from the drill
              // rather than copied into the hint, so the two cannot drift.
              const last = i === drill.hints.length - 1;
              const code = last ? drill.solution : hint.code;
              return (
                <div className="hint" key={hint.label}>
                  <div className="hint-head">
                    <span className="hint-rung">{i + 1}</span>
                    <strong>{last ? "The whole thing" : hint.label}</strong>
                  </div>
                  <p>{hint.body}</p>
                  {code && <pre className="hint-code">{highlightCode(code)}</pre>}
                  {last && <p className="muted small">{drill.complexity}</p>}
                </div>
              );
            })}

            {!locked && hintsTaken < drill.hints.length && (
              <button className="hint-take" onClick={takeHint}>
                {hintsTaken === 0
                  ? "Take a hint"
                  : hintsTaken === drill.hints.length - 1
                    ? "Show the whole solution"
                    : "Next hint"}
                <span className="hint-cost">
                  keeps {Math.round((HINT_KEEPS[hintsTaken + 1] ?? 0.4) * 100)}% of the score
                </span>
              </button>
            )}
          </div>
        </section>

        <section className="panel editor-panel">
          <div className="toolbar">
            <button className="primary" onClick={run} disabled={running || locked}>
              {running ? "Running…" : "Run tests"}
            </button>
            <span className="hint">⌘⏎</span>
            <span className="spacer" />
            <button onClick={() => setCode(drill.starter)} disabled={locked}>
              Reset
            </button>
            {/* Skip records nothing and moves the schedule not at all. Charging
                a failure for "I have seen this too recently" would make the
                schedule worse, and hiding the option makes people sit through a
                repeat resentfully instead of practising something. */}
            <button
              onClick={takeHint}
              disabled={locked || hintsTaken >= drill.hints.length}
              title="Each rung shows a real piece of the solution, and the last one shows all of it. Caps the grade, does not fail the attempt."
            >
              {/* Clamped: once every rung is taken this read "Hint 4/3". */}
              {hintsTaken === 0
                ? "Hint"
                : `Hint ${Math.min(hintsTaken + 1, drill.hints.length)}/${drill.hints.length}`}
            </button>
            <button onClick={onNext} disabled={locked} title="Move on. Does not count as an attempt.">
              Skip
            </button>
            <button className="danger" onClick={giveUp} disabled={locked}>
              Give up
            </button>
          </div>
          <CodeEditor
            key={drill.id}
            value={code}
            onChange={setCode}
            onRun={run}
            onPasteBlocked={() => {
              setToast("Paste is blocked. Type it out, that is the drill.");
              analytics.pasteBlocked({ drillId: drill.id });
            }}
            readOnly={locked}
            syntaxError={result?.error?.syntax ?? null}
          />
        </section>
      </div>

      {guestVerdict && (
        <div className="verdict verdict-guest">
          <div
            className={`verdict-grade grade-${guestVerdict.passed && !guestVerdict.revealed ? 2 : 0}`}
          >
            {guestVerdict.passed && !guestVerdict.revealed ? "Passed" : "Not yet"}
          </div>
          <div className="verdict-detail">
            <div>
              {formatDuration(Math.min(guestVerdict.elapsedMs, drill.timeBudgetMs * 10))} · {runs}{" "}
              {runs === 1 ? "run" : "runs"} · nothing was saved.
            </div>
            {/* The ask, and it names what the account is actually for. Offering
                to "save your score" would be selling the number. The schedule is
                the product and it is the only part a guest genuinely cannot
                have, so it is the only thing worth asking for. */}
            <div className="verdict-ask">
              {guestVerdict.passed && !guestVerdict.revealed
                ? "You will have lost this in about three weeks. An account is what brings it back the day before you would have."
                : "This is the one worth repeating. An account is what schedules it, starting twenty minutes from now."}
            </div>
          </div>
          <div className="verdict-actions">
            <Link className="btn primary" to="/signup">
              Create an account
            </Link>
            <button onClick={onNext}>Try another</button>
          </div>
          <div className="verdict-foot">
            Free, and it stays free. The account holds your schedule, it does not unlock problems:
            every one of them is already open to you.
          </div>
        </div>
      )}

      {graded && (
        <div className="verdict">
          <div className={`verdict-grade grade-${graded.grade}`}>{GRADE_LABEL[graded.grade]}</div>
          <div className="verdict-score">
            +{graded.score} <span>pts</span>
          </div>
          {graded.score > 0 && (
            <div className="verdict-maths" title="How the score was built">
              {graded.breakdown.base} base × {graded.breakdown.speed} time × {graded.breakdown.runs}{" "}
              runs{graded.breakdown.hints < 1 && <> × {graded.breakdown.hints} hints</>} ×{" "}
              {graded.breakdown.streak} streak
            </div>
          )}
          <div className="verdict-detail">
            {formatDuration(Math.min(Date.now() - startedAt.current, drill.timeBudgetMs * 10))} ·{" "}
            {runs} {runs === 1 ? "run" : "runs"} ·{" "}
            {graded.grade === 0
              ? "Reset. This one comes back in 20 minutes."
              : `Next up in ${formatInterval(graded.progress.intervalDays)}.`}
            {graded.ahead && graded.grade > 0 && " Early rep, so it moved half as far."}
            {hintsTaken > 0 &&
              graded.grade > 0 &&
              ` ${hintsTaken === 1 ? "One hint" : `${hintsTaken} hints`}, so it comes back sooner.`}
          </div>
          {submitError && <div className="verdict-error">{submitError}</div>}
          <button className="primary" onClick={onNext}>
            Next drill
          </button>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function formatInterval(days: number): string {
  if (days < 1) return "under a day";
  if (days < 2) return "a day";
  if (days < 45) return `${Math.round(days)} days`;
  return `${Math.round(days / 30)} months`;
}

/**
 * The offending line with the two around it and a caret under the column, the
 * way a compiler reports one. The editor already underlines the same spot; this
 * exists so the panel you are reading is self-contained rather than making you
 * look across at the editor to find out what it is talking about.
 */
function CodeFrame({ code, line, column }: { code: string; line: number; column: number }) {
  const lines = code.split("\n");
  const index = line - 1;
  const first = Math.max(0, index - 2);
  const last = Math.min(lines.length - 1, index + 1);
  const width = String(last + 1).length;

  const rows: ReactNode[] = [];
  for (let i = first; i <= last; i++) {
    const number = String(i + 1).padStart(width, " ");
    rows.push(
      <div key={i} className={i === index ? "frame-line is-error" : "frame-line"}>
        <span className="frame-number">{number}</span>
        <span>{lines[i]}</span>
      </div>,
    );
    if (i === index) {
      // Clamp: sucrase reports a 1-based column, and at end-of-input it can sit
      // one past the last character.
      const caretAt = Math.max(0, Math.min(column - 1, (lines[i] ?? "").length));
      rows.push(
        <div key={`${i}-caret`} className="frame-line frame-caret">
          <span className="frame-number">{" ".repeat(width)}</span>
          <span>{`${" ".repeat(caretAt)}^`}</span>
        </div>,
      );
    }
  }
  return <pre className="code-frame">{rows}</pre>;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

/**
 * One test result.
 *
 * A failure shows three things, in this order: what the case is called, what
 * actually happened (the call, the expectation, the result), and why the case
 * exists at all. The last one is the part that teaches: a message tells you what
 * broke, `about` tells you which rule it was protecting, which is usually the
 * thing you misread in the problem.
 */
function Outcome({
  name,
  passed,
  message,
  about,
}: {
  name: string;
  passed: boolean;
  message?: string;
  about?: string;
}) {
  return (
    <div className={`outcome ${passed ? "pass" : "fail"}`}>
      <span className="marker">{passed ? "✓" : "✕"}</span>
      <div>
        <span className="outcome-name">{name}</span>
        {message && <pre>{message}</pre>}
        {!passed && about && <p className="outcome-about">{about}</p>}
      </div>
    </div>
  );
}
